import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, usePermissions, useTranslate } from "@refinedev/core";
import { ActionBar, Button, Field, FormCard, SectionHeader, SelectField } from "@fawkes/ui";
import { idOf, keuzes, labelVan, useFinancieleOpties } from "../pensiontarief/financieleOpties";
import { Plus } from "lucide-react";

import "./PensionReserveringsproducten.css";

/**
 * De reserveringsproducten van een vestiging: de extra diensten die per reservering verkocht worden.
 *
 * <p>Een annuleringsverzekering, een medifonds, een borg. Dit scherm beheerde er één -- de verzekering
 * stond als instelling per vestiging en daar paste er maar één in. Nu is het een lijst waar je er zelf
 * bij kunt maken, want een pension verkoopt er meer en ze hebben allemaal dezelfde eigenschappen: een
 * bedrag, een manier waarop dat bedrag een prijs wordt, btw en eventueel een minimum.
 *
 * <p><b>Een product van het systeem gaat niet weg.</b> De reserveringswizard vraagt met name om de
 * annuleringsverzekering; die kun je op inactief zetten -- dat is wat "niet meer aanbieden" betekent --
 * maar niet verwijderen, want dan valt het vinkje stil weg uit een scherm dat er niets van weet.
 */

type Berekening = "PER_RESERVERING" | "PER_DIER" | "PER_NACHT" | "PER_DIER_PER_NACHT" | "PERCENTAGE";

const BEREKENINGEN: Berekening[] = [
  "PER_RESERVERING", "PER_DIER", "PER_NACHT", "PER_DIER_PER_NACHT", "PERCENTAGE",
];

interface Product {
  id: number;
  /** Gevuld bij een product waar het systeem zelf naar verwijst; dat gaat niet weg. */
  sleutel: string | null;
  naam: string;
  bedrag: number | null;
  btwPercentage: number;
  berekening: Berekening;
  percentage: number | null;
  minimum: number | null;
  grootboekRekeningId: number | null;
  kostenPlaatsId: number | null;
  projectCodeId: number | null;
  actief: boolean;
}

/** Leeg is niet nul maar "niet ingesteld". */
function getal(waarde: string): number | null {
  const schoon = waarde.trim().replace(",", ".");
  if (schoon === "") {
    return null;
  }
  const n = Number(schoon);
  return Number.isFinite(n) ? n : Number.NaN;
}

function ongeldigGetal(waarde: string, max: number): boolean {
  const n = getal(waarde);
  return n != null && (Number.isNaN(n) || n < 0 || n >= max);
}

export function PensionReserveringsproducten({ vestigingId, onBack }: {
  vestigingId?: number;
  onBack?: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  // Zonder vestiging kiest de api de vestiging waarin je werkt; zo staat hetzelfde scherm ook bij Producten.
  const vraag = vestigingId == null ? "" : `?vestigingId=${vestigingId}`;
  const url = `pension/reserveringsproducten${vraag}`;
  // Zonder retry:false blijft "Laden..." zeven seconden staan als de api dit endpoint niet kent, en
  // daarna nog een keer bij elke poging. Een fout hoort meteen een fout te zijn; een eeuwige spinner
  // laat iemand wachten op iets wat niet komt.
  const { data, isLoading, isError, refetch } = useCustom<Product[]>({
    url,
    method: "get",
    queryOptions: { retry: false },
  });
  const { data: rechten } = usePermissions<string[]>();
  const magBeheren = (rechten ?? []).includes("ROLE_PENSION_ADMIN");

  const lijst = Array.isArray(data?.data) ? data.data : [];
  const [bewerkt, setBewerkt] = useState<Product | "nieuw" | null>(null);

  const euro = (n: number | null) =>
    n == null ? null : new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(n);

  /** Wat dit product kost, in één regel: het bedrag of percentage met de rekenwijze erbij. */
  const opties = useFinancieleOpties();

  const prijsregel = (p: Product): string => {
    const basis = p.berekening === "PERCENTAGE"
      ? p.percentage == null ? null : `${p.percentage}%`
      : euro(p.bedrag);
    if (basis == null) {
      return t("pensionAnnuleringsverzekering.geenBedrag");
    }
    const minimum = p.minimum == null
      ? null
      : t("pensionReserveringsproducten.minimumVan", { args: [euro(p.minimum) ?? ""] });
    return [`${basis} ${t(`pensionReserveringsproducten.per.${p.berekening}`)}`, minimum]
      .filter(Boolean)
      .join(" · ");
  };

  return (
    <FormCard>
      <SectionHeader>{t("pensionReserveringsproducten.titel")}</SectionHeader>
      <div className="acc-beheer">
        <p className="acc-leeg">{t("pensionReserveringsproducten.uitleg")}</p>

        {isLoading ? (
          <p className="fawkes-muted">{t("common.loading")}</p>
        ) : isError ? (
          <p className="fawkes-error">{t("pensionAnnuleringsverzekering.laden")}</p>
        ) : (
          <>
            {lijst.length === 0 ? (
              <p className="acc-leeg">{t("pensionReserveringsproducten.nogGeen")}</p>
            ) : (
              <ul className="rp-lijst">
                {lijst.map((p) => (
                  <li key={p.id} data-inactief={!p.actief || undefined} data-testid={`product-${p.id}`}>
                    <span className="rp-lijst__tekst">
                      <strong>{p.naam}</strong>
                      <span>{prijsregel(p)}</span>
                      {p.grootboekRekeningId != null && (
                        <span>{labelVan(opties.grootboekRekeningen, p.grootboekRekeningId)}</span>
                      )}
                    </span>
                    {!p.actief && <span className="rp-lijst__uit">{t("pensionReserveringsproducten.inactief")}</span>}
                    {magBeheren && (
                      <button type="button" className="acc-knop" onClick={() => setBewerkt(p)}>
                        {t("common.edit")}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {magBeheren ? (
              <div className="acc-voet">
                <Button onClick={() => setBewerkt("nieuw")}>
                  <Plus size={15} aria-hidden="true" />
                  {t("pensionReserveringsproducten.nieuw")}
                </Button>
              </div>
            ) : (
              <p className="acc-leeg">{t("pensionAnnuleringsverzekering.alleenLezen")}</p>
            )}
          </>
        )}
      </div>

      {bewerkt && (
        <ProductFormulier
          product={bewerkt === "nieuw" ? null : bewerkt}
          vraag={vraag}
          onSluit={() => setBewerkt(null)}
          onKlaar={() => {
            setBewerkt(null);
            void refetch();
          }}
        />
      )}

      {onBack && (
        <ActionBar>
          <Button variant="secondary" onClick={onBack}>
            {t("common.back")}
          </Button>
        </ActionBar>
      )}
    </FormCard>
  );
}

/**
 * Eén product instellen: hoe het heet, wat het kost en hoe dat bedrag een prijs wordt.
 *
 * <p>Het rekenvoorbeeld onderaan rekent met twee dieren, vier nachten en een verblijf van € 240,00. Dat
 * staat erbij omdat "per dier per nacht" pas iets betekent als je ziet wat het met een echte boeking
 * doet -- en dat is precies waar iemand zich op verkijkt.
 */
function ProductFormulier({ product, vraag, onSluit, onKlaar }: {
  product: Product | null;
  vraag: string;
  onSluit: () => void;
  onKlaar: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { mutate, isLoading: bezig } = useCustomMutation();

  const [naam, setNaam] = useState(product?.naam ?? "");
  const [bedrag, setBedrag] = useState(product?.bedrag == null ? "" : product.bedrag.toFixed(2));
  const [btw, setBtw] = useState(String(product?.btwPercentage ?? 21));
  const [berekening, setBerekening] = useState<Berekening>(product?.berekening ?? "PER_RESERVERING");
  const [percentage, setPercentage] = useState(product?.percentage == null ? "" : String(product.percentage));
  const [minimum, setMinimum] = useState(product?.minimum == null ? "" : product.minimum.toFixed(2));
  const [grootboek, setGrootboek] = useState(product?.grootboekRekeningId?.toString() ?? "");
  const [kostenplaats, setKostenplaats] = useState(product?.kostenPlaatsId?.toString() ?? "");
  const [projectcode, setProjectcode] = useState(product?.projectCodeId?.toString() ?? "");
  const [actief, setActief] = useState(product?.actief ?? true);
  const [fout, setFout] = useState<string | null>(null);
  const opties = useFinancieleOpties();

  useEffect(() => {
    setFout(null);
  }, [naam, bedrag, btw, berekening, percentage, minimum, grootboek, kostenplaats, projectcode, actief]);

  const perPercentage = berekening === "PERCENTAGE";
  const bedragGetal = getal(bedrag);
  const percentageGetal = getal(percentage);
  const minimumGetal = getal(minimum);
  const btwGetal = Number(btw);
  const geldig =
    naam.trim() !== "" &&
    !ongeldigGetal(bedrag, 100000) &&
    !ongeldigGetal(minimum, 100000) &&
    !ongeldigGetal(percentage, 100.0001) &&
    Number.isInteger(btwGetal) &&
    btwGetal >= 0 &&
    btwGetal <= 100 &&
    (!perPercentage || (percentageGetal != null && percentageGetal > 0)) &&
    (perPercentage || bedragGetal != null || minimumGetal == null);
  const aangeboden = perPercentage ? percentageGetal != null && percentageGetal > 0 : bedragGetal != null && bedragGetal > 0;
  // Een leeg formulier is niet fout maar nog niet af: de rode regel gaat over bedragen die buiten hun
  // grenzen vallen. Stond hij aan zodra "geldig" niet opging, dan begon elk nieuw product in het rood.
  const bedragenFout =
    ongeldigGetal(bedrag, 100000) ||
    ongeldigGetal(minimum, 100000) ||
    ongeldigGetal(percentage, 100.0001) ||
    !Number.isInteger(btwGetal) ||
    btwGetal < 0 ||
    btwGetal > 100 ||
    (!perPercentage && bedragGetal == null && minimumGetal != null);

  const voorbeeld = (): string | null => {
    if (!aangeboden) {
      return null;
    }
    const euro = (n: number) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(n);
    const basis = perPercentage ? ((percentageGetal ?? 0) * 240) / 100 : (bedragGetal ?? 0);
    const rauw =
      berekening === "PER_DIER"
        ? basis * 2
        : berekening === "PER_NACHT"
          ? basis * 4
          : berekening === "PER_DIER_PER_NACHT"
            ? basis * 8
            : basis;
    const premie = minimumGetal != null && rauw < minimumGetal ? minimumGetal : rauw;
    return t("pensionAnnuleringsverzekering.voorbeeld", { args: [euro(premie)] });
  };

  const bewaar = () => {
    if (!geldig) {
      return;
    }
    setFout(null);
    mutate(
      {
        url: product
          ? `pension/reserveringsproducten/${product.id}${vraag}`
          : `pension/reserveringsproducten${vraag}`,
        method: product ? "put" : "post",
        values: {
          naam: naam.trim(),
          bedrag: perPercentage ? null : bedragGetal,
          btwPercentage: btwGetal,
          berekening,
          percentage: perPercentage ? percentageGetal : null,
          minimum: minimumGetal,
          grootboekRekeningId: idOf(grootboek),
          kostenPlaatsId: idOf(kostenplaats),
          projectCodeId: idOf(projectcode),
          actief,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: onKlaar,
        onError: (e) =>
          setFout((e as { message?: string } | null)?.message ?? t("pensionAnnuleringsverzekering.fout")),
      },
    );
  };

  const verwijder = () => {
    if (!product) {
      return;
    }
    setFout(null);
    mutate(
      {
        url: `pension/reserveringsproducten/${product.id}${vraag}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: onKlaar,
        onError: (e) =>
          setFout((e as { message?: string } | null)?.message ?? t("pensionAnnuleringsverzekering.fout")),
      },
    );
  };

  return (
    <form
      className="rp-formulier"
      aria-label={t(product ? "pensionReserveringsproducten.wijzigen" : "pensionReserveringsproducten.nieuw")}
      onSubmit={(e) => {
        e.preventDefault();
        bewaar();
      }}
    >
      <h4 className="rp-formulier__kop">
        {t(product ? "pensionReserveringsproducten.wijzigen" : "pensionReserveringsproducten.nieuw")}
      </h4>

      <div className="rp-velden">
        <Field
          label={t("pensionReserveringsproducten.naam")}
          value={naam}
          maxLength={100}
          data-testid="product-naam"
          onChange={(e) => setNaam(e.target.value)}
        />
        <SelectField
          label={t("pensionAnnuleringsverzekering.berekening")}
          value={berekening}
          data-testid="product-berekening"
          onChange={(e) => setBerekening(e.target.value as Berekening)}
          options={BEREKENINGEN.map((b) => ({
            value: b,
            label: t(`pensionAnnuleringsverzekering.berekening.${b}`),
          }))}
        />
      </div>
      <p className="rp-uitleg">{t(`pensionAnnuleringsverzekering.berekeningUitleg.${berekening}`)}</p>

      <div className="rp-velden">
        {perPercentage ? (
          <Field
            label={t("pensionAnnuleringsverzekering.percentage")}
            type="number"
            min={0}
            max={100}
            step="0.01"
            inputMode="decimal"
            data-testid="product-percentage"
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
          />
        ) : (
          <Field
            label={t(`pensionAnnuleringsverzekering.bedrag.${berekening}`)}
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            data-testid="product-bedrag"
            value={bedrag}
            onChange={(e) => setBedrag(e.target.value)}
          />
        )}
        <Field
          label={t("pensionAnnuleringsverzekering.minimum")}
          type="number"
          min={0}
          step="0.01"
          inputMode="decimal"
          value={minimum}
          onChange={(e) => setMinimum(e.target.value)}
        />
        <Field
          label={t("pensionAnnuleringsverzekering.btw")}
          type="number"
          min={0}
          max={100}
          step={1}
          value={btw}
          onChange={(e) => setBtw(e.target.value)}
        />
      </div>

      {/* Waar dit product op geboekt wordt. Een verzekering en een medifonds gaan naar andere
          rekeningen, en dat achteraf uit de omschrijving afleiden is werk dat de boekhouding niet
          hoort te hebben. Leeg mag: dan geldt wat er elders is afgesproken. */}
      <div className="rp-velden">
        <SelectField
          label={t("pensionTarief.hist.grootboek")}
          value={grootboek}
          data-testid="product-grootboek"
          onChange={(e) => setGrootboek(e.target.value)}
          options={keuzes(opties.grootboekRekeningen, t("pensionTarief.financieel.geen"))}
        />
        <SelectField
          label={t("pensionTarief.hist.kostenplaats")}
          value={kostenplaats}
          onChange={(e) => setKostenplaats(e.target.value)}
          options={keuzes(opties.kostenPlaatsen, t("pensionTarief.financieel.geen"))}
        />
        <SelectField
          label={t("pensionTarief.hist.projectcode")}
          value={projectcode}
          onChange={(e) => setProjectcode(e.target.value)}
          options={keuzes(opties.projectCodes, t("pensionTarief.financieel.geen"))}
        />
      </div>

      <label className="rp-actief">
        <input type="checkbox" checked={actief} data-testid="product-actief"
          onChange={(e) => setActief(e.target.checked)} />
        {t("pensionReserveringsproducten.aanbieden")}
      </label>

      {!aangeboden && <p className="rp-uitleg">{t("pensionAnnuleringsverzekering.geenBedrag")}</p>}
      {aangeboden && geldig && <p className="rp-voorbeeld">{voorbeeld()}</p>}
      {bedragenFout && <p className="rp-fout">{t("pensionAnnuleringsverzekering.ongeldig")}</p>}
      {fout && <p className="rp-fout" role="alert">{fout}</p>}

      <div className="rp-voet">
        {/* Alleen een zelfgemaakt product gaat weg; de api weigert de rest met 409, en een knop die dat
            niet weet laat iemand twee keer proberen. */}
        {product && product.sleutel == null && (
          <button type="button" className="rp-weg" disabled={bezig} onClick={verwijder}>
            {t("common.delete")}
          </button>
        )}
        <Button variant="secondary" onClick={onSluit} disabled={bezig}>{t("common.cancel")}</Button>
        <Button type="submit" disabled={bezig || !geldig}>{t("common.save")}</Button>
      </div>
    </form>
  );
}
